import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DynamicTextSkeleton from '../../components/DynamicTextSkeleton/DynamicTextSkeleton';
import { API_DOMAIN, CHINA_VERSION } from '../../globals';
import { setDynamicTranslation } from '../../redux/slices/appSettingsSlice/appSettingsSlice';
import useAxios from '../useAxios/useAxios';

const BATCH_QUEUE_KEY = 'translation_batch_queue';
const BATCH_TIMER_KEY = 'translation_batch_timer';
const BATCH_PROCESSING_KEY = 'translation_batch_processing';

// helpers

const getBatchQueue = () => {
  try {
    const stored = localStorage.getItem(BATCH_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const setBatchQueue = (queue) => {
  try {
    localStorage.setItem(BATCH_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to set batch queue:', error);
  }
};

const getBatchTimer = () => {
  try {
    const stored = localStorage.getItem(BATCH_TIMER_KEY);
    return stored ? parseInt(stored, 10) : null;
  } catch {
    return null;
  }
};

const setBatchTimer = (timestamp) => {
  try {
    localStorage.setItem(BATCH_TIMER_KEY, timestamp.toString());
  } catch (error) {
    console.error('Failed to set batch timer:', error);
  }
};

const clearBatchTimer = () => {
  try {
    localStorage.removeItem(BATCH_TIMER_KEY);
  } catch (error) {
    console.error('Failed to clear batch timer:', error);
  }
};

const isBatchProcessing = () => {
  try {
    const stored = localStorage.getItem(BATCH_PROCESSING_KEY);
    if (!stored) return false;

    const timestamp = parseInt(stored, 10);
    // if longer than 10 seconds, consider it stale/error
    return Date.now() - timestamp < 10000;
  } catch {
    return false;
  }
};

const setBatchProcessing = (processing) => {
  try {
    if (processing) {
      localStorage.setItem(BATCH_PROCESSING_KEY, Date.now().toString());
    } else {
      localStorage.removeItem(BATCH_PROCESSING_KEY);
    }
  } catch (error) {
    console.error('Failed to set batch processing:', error);
  }
};

const useTranslate = () => {
  const axios = useAxios();
  const dispatch = useDispatch();

  const currentLanguage = useSelector(
    (state) => state.appSettings.languages.current
  );
  const staticTranslations = useSelector(
    (state) => state.appSettings.translations
  );
  const dynamicTranslations = useSelector(
    (state) => state.appSettings.dynamicTranslations
  );

  // Force re-render when translations complete
  const [, setForceUpdate] = useState({});
  const forceUpdate = useCallback(() => setForceUpdate({}), []);

  // static translation function
  const t = useCallback(
    (text) => staticTranslations?.[currentLanguage]?.[text] || text,
    [currentLanguage, staticTranslations]
  );

  // replace variables in fragments
  const processFragments = useCallback((fragments, variables = {}) => {
    const processedFragments = fragments.map((fragment) => {
      if (typeof fragment === 'string' && RegExp(/^\{\w+\}$/).exec(fragment)) {
        const key = fragment.substring(1, fragment.length - 1);
        return variables && variables[key] !== undefined
          ? variables[key]
          : fragment;
      }
      return fragment;
    });

    // join fragments with smart spacing logic
    let finalText = '';
    for (let i = 0; i < processedFragments.length; i++) {
      const fragment = processedFragments[i];
      if (i === 0) {
        finalText = fragment;
        continue;
      }
      if (typeof fragment === 'string' && fragment.startsWith('-')) {
        finalText += fragment.substring(1);
      } else {
        finalText += ' ' + fragment;
      }
    }

    return finalText.trim();
  }, []);

  const processBatch = useCallback(async () => {
    if (isBatchProcessing()) {
      return;
    }

    const queue = getBatchQueue();
    if (queue.length === 0) return;

    setBatchProcessing(true);

    // clear queue and timer
    setBatchQueue([]);
    clearBatchTimer();

    // remove duplicates and create batch object
    const uniqueTexts = [...new Set(queue)];
    const batchObject = {};

    uniqueTexts.forEach((text, index) => {
      batchObject[`text_${index}`] = text;
    });

    try {
      const response = await axios.post(
        `${API_DOMAIN}/dynamic-translation`,
        batchObject
      );

      if (response?.data) {
        // Process each translation and store in redux
        Object.entries(response.data).forEach(([key, translation]) => {
          const index = parseInt(key.replace('text_', ''), 10);
          const originalText = uniqueTexts[index];

          if (originalText && translation) {
            dispatch(
              setDynamicTranslation({
                language: currentLanguage,
                original: originalText,
                translation: translation,
              })
            );
          }
        });

        // as we need all instances of the hook to re-render
        // outside of the react lifecycle
        // we dispatch a custom event
        window.dispatchEvent(new CustomEvent('translationBatchComplete'));
      }
    } catch (error) {
      console.error('❌ Batch translation failed:', error);
    } finally {
      setBatchProcessing(false);
    }
  }, [axios, currentLanguage, dispatch]);

  const checkBatchTimer = useCallback(() => {
    const timerTimestamp = getBatchTimer();
    if (timerTimestamp && Date.now() >= timerTimestamp) {
      processBatch();
    }
  }, [processBatch]);

  const addToBatch = useCallback(
    (text) => {
      // Get current queue
      const currentQueue = getBatchQueue();

      // Add text if not already in queue
      if (!currentQueue.includes(text)) {
        const newQueue = [...currentQueue, text];
        setBatchQueue(newQueue);
      }

      // 1 second intervals!
      const batchTime = Date.now() + 1000;
      setBatchTimer(batchTime);

      setTimeout(checkBatchTimer, 1100);
    },
    [checkBatchTimer]
  );

  // Listen for batch completion events from other hook instances
  useEffect(() => {
    const handleBatchComplete = () => {
      forceUpdate();
    };

    window.addEventListener('translationBatchComplete', handleBatchComplete);

    return () => {
      window.removeEventListener(
        'translationBatchComplete',
        handleBatchComplete
      );
    };
  }, [forceUpdate]);

  // Main dynamic translation function
  const dt = useCallback(
    (fragments, variables, testId) => {
      const finalText = processFragments(fragments, variables);

      // For non-china version, return processed text directly
      // and immediately
      if (!CHINA_VERSION || currentLanguage !== 'zh-cn') {
        return finalText;
      }

      // Check if translation is already cached
      const cachedTranslation =
        dynamicTranslations?.[currentLanguage]?.[finalText];
      if (cachedTranslation) {
        return (
          <DynamicTextSkeleton
            text={cachedTranslation}
            isLoading={false}
            testId={testId}
          />
        );
      }

      addToBatch(finalText);

      // return skeleton component while loading

      return (
        <DynamicTextSkeleton
          text={finalText}
          isLoading={true}
          testId={testId}
        />
      );
    },
    [processFragments, currentLanguage, dynamicTranslations, addToBatch]
  );

  const constants = useMemo(
    () => ({
      errorMessage: `${t('Oops!')} ${t('Something went wrong.')} ${t(
        'Please refresh your browser or try again later.'
      )}`,
      prefixOptions: [
        { option: t('Dr.'), code: 'Dr' },
        { option: t('Prof.'), code: 'Prof' },
        { option: t('Miss.'), code: 'Miss' },
        { option: t('Mx.'), code: 'Mx' },
        { option: t('Mrs.'), code: 'Mrs' },
        { option: t('Ms.'), code: 'Ms' },
        { option: t('Mr.'), code: 'Mr' },
      ],
      formRules: {
        emailRule: {
          pattern: {
            message: t('Please provide a valid email address'),
            value:
              /^(?!.*\.\.)[A-Z0-9._%+-]+@[A-Z0-9-]+(?:\.[A-Z0-9]+)*\.[A-Z]{2,63}$/i,
          },
          required: t('Email is required'),
        },
      },
    }),
    [t]
  );

  return { t, dt, constants };
};

export default useTranslate;
